import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useCreateCustomLineItem } from '@/hooks/use-financial-data';
import type {
  CustomLineItemCategory,
  CustomLineItemPLCategory,
  CustomLineItemBSCategory,
} from '@/types/financial';

export interface AddCustomLineItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityRecordId: string;
  statementType: 'profit_loss' | 'balance_sheet';
  /** Pre-filled category based on which section's "+" was clicked */
  defaultCategory: CustomLineItemCategory;
  /** Display label for the category (e.g., "Revenue", "Current Assets") */
  categoryLabel: string;
  /**
   * When set, the category dropdown is locked to this value.
   * Maps to a CustomLineItemCategory value.
   * e.g. 'operating_expense' locks to Operating Expense and shows subcategory dropdown.
   */
  preselectedCategory?: CustomLineItemCategory;
}

// Category options by statement type
const PL_CATEGORIES: Array<{ value: CustomLineItemPLCategory; label: string }> = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'cost_of_sales', label: 'Cost of Sales' },
  { value: 'operating_expense', label: 'Operating Expense' },
  { value: 'non_operating_expense', label: 'Non-Operating Expense' },
];

const BS_CATEGORIES: Array<{ value: CustomLineItemBSCategory; label: string }> = [
  { value: 'current_asset', label: 'Current Asset' },
  { value: 'non_current_asset', label: 'Non-Current Asset' },
  { value: 'current_liability', label: 'Current Liability' },
  { value: 'non_current_liability', label: 'Non-Current Liability' },
  { value: 'equity', label: 'Equity' },
];

// Subcategory options for Operating Expense
const OPEX_SUBCATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'personnel', label: 'Personnel' },
  { value: 'occupancy', label: 'Occupancy' },
  { value: 'admin_office', label: 'Admin & Office' },
  { value: 'sales_marketing', label: 'Sales & Marketing' },
  { value: 'motor_vehicle', label: 'Motor Vehicle' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'other', label: 'Other' },
];

// Subcategory options for Current Asset (Balance Sheet)
const BS_CURRENT_ASSET_SUBCATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'cash_and_bank', label: 'Cash & Bank' },
  { value: 'receivables', label: 'Receivables' },
  { value: 'inventory_wip', label: 'Inventory & WIP' },
  { value: 'other_current', label: 'Other Current' },
];

// Subcategory options for Current Liability (Balance Sheet)
const BS_CURRENT_LIABILITY_SUBCATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'payables', label: 'Payables' },
  { value: 'tax_and_employee', label: 'Tax & Employee' },
  { value: 'borrowings_cl', label: 'Borrowings' },
  { value: 'other_cl', label: 'Other' },
];

/**
 * Add Custom Line Item Dialog
 *
 * Sprint 11 - Creates entity-level custom line item definitions.
 * Sprint 13 F012 - Supports preselectedCategory (locked dropdown) and
 *   subcategory dropdown for Operating Expense.
 *
 * The dialog is opened from the "Add Row" button in spreadsheet sections
 * and creates a definition that appears across all periods.
 *
 * Amounts are NOT entered here - they are entered via spreadsheet cells (per-period upsert).
 */
export const AddCustomLineItemDialog: React.FC<AddCustomLineItemDialogProps> = ({
  open,
  onOpenChange,
  entityRecordId,
  statementType,
  defaultCategory,
  categoryLabel,
  preselectedCategory,
}) => {
  const effectiveCategory = preselectedCategory ?? defaultCategory;
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<CustomLineItemCategory>(effectiveCategory);
  const [subcategory, setSubcategory] = useState<string>('');
  const { toast } = useToast();
  const createMutation = useCreateCustomLineItem();

  const isCategoryLocked = !!preselectedCategory;
  const showSubcategory =
    category === 'operating_expense' ||
    category === 'current_asset' ||
    category === 'current_liability';

  // Sync category when preselectedCategory or defaultCategory changes
  useEffect(() => {
    setCategory(preselectedCategory ?? defaultCategory);
  }, [preselectedCategory, defaultCategory]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setLabel('');
      setCategory(preselectedCategory ?? defaultCategory);
      setSubcategory('');
    }
  }, [open, preselectedCategory, defaultCategory]);

  const handleSave = () => {
    const trimmedLabel = label.trim();

    if (!trimmedLabel) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a label for the custom line item',
        variant: 'destructive',
      });
      return;
    }

    if (!category) {
      toast({
        title: 'Validation Error',
        description: 'Please select a category',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate(
      {
        entityFinancialRecordId: entityRecordId,
        statementType,
        category,
        label: trimmedLabel,
        ...(showSubcategory && subcategory ? { subcategory } : {}),
      },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Custom line item created successfully',
          });
          onOpenChange(false);
        },
        onError: (error: Error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to create custom line item',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const categories = statementType === 'profit_loss' ? PL_CATEGORIES : BS_CATEGORIES;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" data-testid="add-custom-line-item-dialog">
        <DialogHeader>
          <DialogTitle>Add Custom Line Item</DialogTitle>
          <DialogDescription>
            Add a custom line item to <strong>{categoryLabel}</strong>. Amounts will be entered in
            the spreadsheet.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              data-testid="custom-line-item-label"
              placeholder="e.g., Consulting Revenue, Equipment Lease"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={createMutation.isPending}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Enter a descriptive label for this line item
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={category}
              onValueChange={(value) => setCategory(value as CustomLineItemCategory)}
              disabled={createMutation.isPending || isCategoryLocked}
            >
              <SelectTrigger
                id="category"
                data-testid="custom-line-item-category"
                className="w-full"
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Category for financial statement grouping
            </p>
          </div>
          {showSubcategory && (
            <div className="grid gap-2">
              <Label htmlFor="subcategory">Subcategory</Label>
              <Select
                value={subcategory || 'none'}
                onValueChange={(value) => setSubcategory(value === 'none' ? '' : value)}
                disabled={createMutation.isPending}
              >
                <SelectTrigger
                  id="subcategory"
                  data-testid="custom-line-item-subcategory"
                  className="w-full"
                >
                  <SelectValue placeholder="Select subcategory (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(category === 'current_asset'
                    ? BS_CURRENT_ASSET_SUBCATEGORIES
                    : category === 'current_liability'
                      ? BS_CURRENT_LIABILITY_SUBCATEGORIES
                      : OPEX_SUBCATEGORIES
                  ).map((sub) => (
                    <SelectItem key={sub.value} value={sub.value}>
                      {sub.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Optional subcategory for grouping within this category
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={createMutation.isPending}
            data-testid="save-custom-line-item"
          >
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Backward compatibility: export old name as alias
export const AddCustomLineItemModal = AddCustomLineItemDialog;
